<template>
  <el-popover
    v-model="visible"
    placement="bottom-start"
    trigger="click"
    popper-class="date-filter-popover"
    :visible-arrow="false"
  >
    <div class="select-time">
      <!-- 标题 -->
      <div class="st-header">
        <div class="st-title">筛选日期</div>
      </div>

      <!-- 快捷选项 -->
      <div class="st-quick-grid">
        <div
          v-for="item in quickOptions"
          :key="item.value"
          class="st-quick-item"
          :class="{ active: activeQuick === item.value }"
          @click="handleQuickSelect(item)"
        >
          {{ item.label }}
        </div>
      </div> 

      <!-- 自定义范围分割线 -->
      <div class="st-divider">
        <span class="st-divider-text">自定义范围</span>
      </div>

      <!-- 时间区间选择 -->
      <div class="st-range">
        <div class="st-range-label">时间区间选择</div>
        <el-date-picker
          v-model="customRange"
          type="daterange"
          range-separator=""
          start-placeholder="年 / 月 / 日"
          end-placeholder="年 / 月 / 日"
          value-format="yyyy-MM-dd"
          format="yyyy / MM / dd"
          :clearable="true"
          popper-class="select-time-picker-popper"
          class="st-date-picker"
          @change="handleCustomChange"
        />
      </div>

      <!-- 底部按钮 -->
      <div class="st-footer">
        <el-button class="st-btn st-btn-reset" @click="handleReset">重置</el-button>
        <el-button class="st-btn st-btn-apply" @click="handleApply">应用</el-button>
      </div>
    </div>

    <div
      slot="reference"
      class="date-filter-trigger"
      :class="{ 'has-value': internalValue && internalValue.length === 2, 'is-active': visible }"
    >
      <span class="date-filter-text">{{ dateFilterLabel }}</span>
      <i
        v-if="internalValue && internalValue.length === 2"
        class="el-icon-circle-close date-filter-clear"
        @click.stop="handleReset"
      ></i>
      <i v-else class="el-icon-arrow-down date-filter-arrow"></i>
    </div>
  </el-popover>
</template>

<script>
export default {
  name: 'SelectTime',
  props: {
    // v-model 绑定的时间区间 [start, end]，格式 yyyy-MM-dd
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // popover 显隐
      visible: false,
      // 内部追踪的值，同步外部 v-model
      internalValue: [],
      // 当前选中的快捷项
      activeQuick: '',
      // 自定义时间区间
      customRange: [],
      // 快捷选项配置
      quickOptions: [
        { label: '今天', value: 'today', days: 0 },
        { label: '最近7天', value: 'last5', days: 7 },
        { label: '最近15天', value: 'last15', days: 15 },
        { label: '最近30天', value: 'last30', days: 30 },
        { label: '最近半年', value: 'last180', days: 180 },
        { label: '最近一年', value: 'last365', days: 365 }
      ]
    }
  },
  computed: {
    dateFilterLabel() {
      if (this.internalValue && this.internalValue.length === 2 && this.internalValue[0] && this.internalValue[1]) {
        return `${this.internalValue[0]} ~ ${this.internalValue[1]}`
      }
      return '日期筛选'
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        const arr = Array.isArray(val) && val.length === 2 ? [val[0], val[1]] : []
        this.internalValue = arr
        if (arr.length === 2) {
          this.customRange = [arr[0], arr[1]]
        } else {
          this.customRange = []
          this.activeQuick = ''
        }
      }
    }
  },
  methods: {
    // 选择快捷项
    handleQuickSelect(item) {
      this.activeQuick = item.value
      const end = new Date()
      const start = new Date()
      if (item.days > 0) {
        start.setDate(end.getDate() - item.days + 1)
      }
      const range = [this.formatDate(start), this.formatDate(end)]
      this.customRange = range
      this.internalValue = range
      this.visible = false
      this.$emit('input', range)
      this.$emit('apply', { type: item.value, range })
      this.$emit('quick-select', { type: item.value, range })
    },

    // 自定义时间变化时清除快捷选中状态
    handleCustomChange(val) {
      this.activeQuick = ''
      this.customRange = val || []
    },

    // 重置
    handleReset() {
      this.activeQuick = ''
      this.customRange = []
      this.internalValue = []
      this.visible = false
      this.$emit('input', [])
      this.$emit('reset')
    },

    // 应用
    handleApply() {
      const range = this.customRange || []
      const validRange = Array.isArray(range) && range.length === 2 ? range : []
      this.internalValue = validRange
      this.visible = false
      this.$emit('input', validRange)
      this.$emit('apply', {
        type: this.activeQuick,
        range: validRange
      })
    },

    // 日期格式化 yyyy-MM-dd
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
  }
}
</script>

<style lang="scss" scoped>
/* ==================== 触发按钮 ==================== */
.date-filter-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 200px;
  height: 32px;
  padding: 0 8px;
  font-size: 14px;
  color: #C0C4CC;
  cursor: pointer;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  transition: all 0.2s;
  user-select: none;
  box-sizing: border-box;

  .date-filter-text {
    flex: 1;
    min-width: 0;
    line-height: 32px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .date-filter-arrow {
    flex-shrink: 0;
    font-size: 14px;
    color: #c0c4cc;
    transition: transform 0.2s;
  }

  .date-filter-clear {
    flex-shrink: 0;
    font-size: 14px;
    color: #c0c4cc;
    transition: color 0.2s;
  }

  &:hover {
    border-color: #c0c4cc;
  }

  &.is-active {
    border-color: #7c5cfc;

    .date-filter-arrow {
      transform: rotate(180deg);
      color: #7c5cfc;
    }
  }

  &.has-value {
    color: #606266;

    .date-filter-arrow,
    .date-filter-clear {
      color: #909399;
    }
  }
}

/* ==================== 日期筛选面板 ==================== */
.select-time {
  width: 320px;
  padding: 20px 18px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

/* 标题区域 */
.st-header {
  text-align: center;
  margin-bottom: 16px;

  .st-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f1f1f;
    line-height: 24px;
  }

  .st-subtitle {
    margin-top: 4px;
    font-size: 12px;
    color: #9a9a9a;
    line-height: 18px;
  }
}

/* 快捷选项网格 */
.st-quick-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.st-quick-item {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f6f8;
  border-radius: 4px;
  font-size: 14px;
  color: #4a4a4a;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: #f9f7ff;
    color: #7c5cfc;
  }

  &.active {
    background: #f9f7ff;
    color: #7c5cfc;
  }
}

/* 分割线 */
.st-divider {
  position: relative;
  text-align: center;
  margin: 10px 0 14px;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    border-top: 1px dashed #e3e3e6;
  }

  .st-divider-text {
    position: relative;
    display: inline-block;
    padding: 0 10px;
    background: #ffffff;
    font-size: 12px;
    color: #9a9a9a;
  }
}

/* 时间区间 */
.st-range {
  margin-bottom: 18px;

  .st-range-label {
    font-size: 13px;
    color: #4a4a4a;
    margin-bottom: 8px;
  }

  .st-date-picker {
    width: 100%;
  }

  ::v-deep .el-date-editor {
    width: 100%;
    height: 38px;
    border-radius: 4px;
    background: #ffffff;
    border: 1px solid #e3e3e6;
    padding: 0 10px;

    &:hover {
      border-color: #c7c7cc;
    }

    &.is-active,
    &.is-focus {
      border-color: #7c5cfc;
    }

    .el-range-input {
      background: transparent;
      font-size: 14px;
      color: #1f1f1f;

      &::placeholder {
        color: #b0b0b5;
      }
    }

    .el-range-separator {
      width: 12px;
      color: #c7c7cc;
    }

    .el-range__icon,
    .el-range__close-icon {
      line-height: 36px;
      color: #9a9a9a;
    }
  }
}

/* 底部按钮 */
.st-footer {
  display: flex;
  gap: 10px;

  .st-btn {
    flex: 1;
    height: 40px;
    border-radius: 4px;
    font-size: 14px;
    padding: 0;
    margin: 0;
  }

  .st-btn-reset {
    flex: 0 0 90px;
    background: #ffffff;
    color: #858D94;
    border: 1px solid #e3e3e6;

    &:hover,
    &:focus {
      background: #ECF0F6;
      border-color: #e0e0e0;
    }
  }

  .st-btn-apply {
    background: #7c5cfc;
    color: #ffffff;
    border: 1px solid #7c5cfc;
    font-weight: 500;

    &:hover,
    &:focus {
      background: #7c5cfc;
      border-color: #7c5cfc;
      color: #ffffff;
    }
  }
}
</style>

<style lang="scss">
.date-filter-popover.el-popover {
  padding: 0;
  border: none;
  border-radius: 12px;
  background: transparent;
  box-shadow: none;
  min-width: auto;
}
</style>
